import { useNavigate } from '@tanstack/react-router'
import { formatOrdinal, tiedRanks } from '@topmeeps/shared'
import { useGames } from '../../api/hooks'
import { MeepleAvatar, NumberCircle } from '../../primitives'
import { draftKeyOf, useRecordPlayDraft } from '../../stores/recordPlayDraft'

export const RecordResultsStep = () => {
  const navigate = useNavigate()
  const groupId = useRecordPlayDraft((s) => s.groupId)
  const gameId = useRecordPlayDraft((s) => s.gameId)
  const participants = useRecordPlayDraft((s) => s.participants)
  const reorderParticipant = useRecordPlayDraft((s) => s.reorderParticipant)
  const toggleTie = useRecordPlayDraft((s) => s.toggleTie)
  const setScore = useRecordPlayDraft((s) => s.setScore)
  const setSubscore = useRecordPlayDraft((s) => s.setSubscore)
  const games = useGames()

  const game = games.data?.find((g) => g.id === gameId)
  const schema = game?.scoreSchema
  // Array order is the canonical finishing order; rank/tiedWithPrevious are
  // kept in sync by the store.
  const ordered = participants
  const tied = tiedRanks(ordered.map((p) => p.rank))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
        Use ↑/↓ to set the finishing order. Tap <strong>Tie</strong> to mark a player as finishing
        level with the one above. Rank drives the rating math.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ordered.map((p, i) => {
          const key = draftKeyOf(p)
          const isTied = tied.has(p.rank)
          return (
            <div
              key={key}
              className="felt-card"
              style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              {i > 0 && (
                <button
                  type="button"
                  className={p.tiedWithPrevious ? 'btn-stamp' : 'btn-ghost'}
                  onClick={() => toggleTie(key)}
                  style={{
                    alignSelf: 'center',
                    padding: '1px 10px',
                    fontSize: 10,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}
                  aria-pressed={p.tiedWithPrevious}
                >
                  {p.tiedWithPrevious ? '= tied with above' : '= tie with above'}
                </button>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <NumberCircle n={p.rank} winner={p.rank === 1} size={26} />
                <MeepleAvatar name={p.displayName} hue={p.avatarHue} size={32} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{p.displayName}</span>
                  {isTied && (
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      Tied for {formatOrdinal(p.rank)}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => reorderParticipant(key, -1)}
                  disabled={i === 0}
                  style={{ padding: '4px 8px' }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => reorderParticipant(key, 1)}
                  disabled={i === ordered.length - 1}
                  style={{ padding: '4px 8px' }}
                >
                  ↓
                </button>
              </div>

              {schema?.scoreField.enabled && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 12,
                    color: 'var(--ink-2)',
                  }}
                >
                  <span style={{ minWidth: 90 }}>{schema.scoreField.label}</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={p.score ?? ''}
                    onChange={(e) => {
                      const v = e.currentTarget.value
                      setScore(key, v === '' ? null : Number(v))
                    }}
                    style={{ width: 90 }}
                  />
                </label>
              )}

              {schema?.subscores.map((sub) => (
                <label
                  key={sub.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 12,
                    color: 'var(--ink-2)',
                  }}
                >
                  <span style={{ minWidth: 90 }}>{sub.label}</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={p.subscores[sub.label] ?? ''}
                    onChange={(e) => {
                      const v = e.currentTarget.value
                      if (v === '') return
                      setSubscore(key, sub.label, Number(v))
                    }}
                    style={{ width: 90 }}
                  />
                </label>
              ))}
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className="btn-stamp"
        onClick={() => {
          if (groupId) navigate({ to: '/group/$id/record/details', params: { id: groupId } })
          else navigate({ to: '/record/details' })
        }}
      >
        Continue
      </button>
    </div>
  )
}
